import React, { useEffect, useContext, useState } from 'react';
import { Link, navigate } from '@reach/router';
import classNames from 'classnames';

import '../../Dashboard/InventoryTable/InventoryTable.scss';

import PopUp from 'components/Common/PopUp/PopUp';
import { IPopUpOptions } from 'models/Dashboard/IPopUpOptions';
import ManageIconGray from 'assets/icons/manage-icon-gray.svg';
import { UserContext } from 'providers/contexts';

export interface ICellData {
  col: IData[];
  key: any;
  data?: any;
}

export interface IData {
  cell?: string;
  imageUrl?: string;
  button?: string;
  color?: string;
  linkTo?: string;
}

export interface IHeaderGroups {
  header: string;
}

export interface IProductCSVHistoryList {
  headerGroups: IHeaderGroups[];
  showHeader?: boolean;
  cellData: ICellData[];
  popUpOptions: IPopUpOptions[];
  placeholder?: string;
}

export interface IProductCSVHistoryListRow {
  headerGroups: IHeaderGroups[];
  rowData: ICellData;
  popUpOptions: IPopUpOptions[];
  onPopUpModal: (itemKey: any, ModalComponent: React.ReactElement) => void;
}

const ProductCSVHistoryList: React.FC<IProductCSVHistoryList> = props => {
  const {
    headerGroups,
    showHeader = true,
    cellData,
    popUpOptions,
    placeholder = 'No CSV history - Uploaded CSVs can be viewed here.',
  } = props;
  const [selectedItem, setSelectedItem] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [ActiveModalComponent, setActiveModalComponent] = useState(null);

  const handlePopUpModal = (itemKey, ModalComponent) => {
    setSelectedItem(itemKey);
    setActiveModalComponent(ModalComponent);
    setModalOpen(true);
  };

  return (
    <div>
      <table className='table'>
        {showHeader && (
          <thead>
            <tr>
              {headerGroups?.map(({ header }) => (
                <th key={header}>{header}</th>
              ))}
            </tr>
          </thead>
        )}

        <tbody>
          {cellData.length === 0 ? (
            <tr>
              <td>{placeholder}</td>
            </tr>
          ) : (
            cellData?.map(rowData => (
              <ProductCSVHistoryListRow
                key={`row-${rowData.key}`}
                headerGroups={headerGroups}
                rowData={rowData}
                popUpOptions={popUpOptions}
                onPopUpModal={handlePopUpModal}
              />
            ))
          )}
        </tbody>
      </table>
      {ActiveModalComponent &&
        modalOpen &&
        React.cloneElement(ActiveModalComponent, {
          open: modalOpen,
          setOpen: setModalOpen,
          data: cellData.find(item => item.key === selectedItem)?.data,
        })}
    </div>
  );
};

const ProductCSVHistoryListRow: React.FC<IProductCSVHistoryListRow> = props => {
  const { headerGroups, rowData, popUpOptions, onPopUpModal } = props;
  const [open, setOpen] = useState(false);
  const { user } = useContext(UserContext);
  const { idToken } = user;

  return (
    <tr>
      {rowData.col?.map(({ linkTo, cell, imageUrl, button, color }, index) => (
        <td
          data-th={headerGroups[index].header}
          key={headerGroups[index].header}
          className={classNames({ link: !!linkTo })}
          onClick={() => linkTo && navigate(linkTo)}
        >
          {imageUrl && (
            <img
              style={{ height: '32px', width: '32px' }}
              className='table-img'
              src={imageUrl}
              alt='csvHistoryItem'
            />
          )}
          {button && (
            <button className={`button-badge color-${color}-bg`}>{button}</button>
          )}
          {cell}
        </td>
      ))}

      <td>
        <img
          className='manage-team__dots'
          onClick={() => setOpen(!open)}
          src={ManageIconGray}
          alt='Manage Icon'
        />

        <PopUp open={open} setOpen={setOpen}>
          {popUpOptions?.map(
            ({ selectItem, to, ModalComponent, onClick, download, disabled = false }) =>
              download ? (
                <li
                  style={disabled ? { pointerEvents: 'none', opacity: '0.5' } : {}}
                  className='pop-up__li'
                  key={selectItem}
                >
                  <a
                    className={`cursor-pointer`}
                    href={`${process.env.REACT_APP_API_BASEURL}/api/parsedspreadsheets/method/download?key=${rowData.data?.key}&auth=${idToken}`}
                    download={`${rowData.data.brand}-${rowData.data.created}-${rowData.data.spreadsheet_id}`}
                    title={`${rowData.data.brand}-${rowData.data.created}-${rowData.data.spreadsheet_id}`}
                  >
                    {selectItem}
                  </a>
                </li>
              ) : (
                <li
                  style={disabled ? { pointerEvents: 'none', opacity: '0.5' } : {}}
                  className='pop-up__li'
                  onClick={() => {
                    setOpen(false);
                    if (ModalComponent) {
                      onPopUpModal?.(rowData.key, ModalComponent);
                    } else if (onClick) {
                      onClick(rowData.data);
                    }
                  }}
                  key={selectItem}
                >
                  {to ? <Link to={to}>{selectItem}</Link> : selectItem}
                </li>
              )
          )}
        </PopUp>
      </td>
    </tr>
  );
};

export default ProductCSVHistoryList;
